import { type ReactNode } from 'react'

import { useCopilotAction } from '@copilotkit/react-core'
import { F0DemoCard } from '@factorialco/f0-react'

const VIDEO_EXTENSIONS = /\.(mp4|webm|mov|ogg|ogv|m3u8)(\?|$)/i

const isVideopreviewMediaUrl = (previewMediaUrl: string): boolean =>
  VIDEO_EXTENSIONS.test(previewMediaUrl)

const PreviewPlaceholder = () => (
  <div
    className="flex min-h-[160px] w-full items-center justify-center bg-f1-background-tertiary"
    aria-hidden
  >
    <span className="text-sm text-f1-foreground-secondary">Preview</span>
  </div>
)

/**
 * Renders a demo card (preview image/video, title, description, CTA) for
 * guided walkthroughs.
 */
export const useDemoCardAction = () => {
  useCopilotAction({
    name: 'AiWidgets.UpsellKit.F0DemoCard',
    description:
      'Display a demo card with optional preview image or video, title, description, and a primary action button (e.g. for demos or guided walkthroughs).',
    parameters: [
      {
        name: 'moduleName',
        type: 'string',
        description:
          'Module name for the icon (e.g. company_projects, benefits, projects, calendar, reports)',
        required: true,
      },
      {
        name: 'description',
        type: 'string',
        description: 'Description text below the title',
        required: true,
      },
      {
        name: 'actionHref',
        type: 'string',
        description: 'Optional previewMediaUrl for the action button (opens when user clicks)',
        required: false,
      },
      {
        name: 'previewMediaUrl',
        type: 'string',
        description:
          'Optional image or video previewMediaUrl for the preview area (type inferred from previewMediaUrl, e.g. .mp4, .webm for video)',
        required: false,
      },
    ],
    available: 'frontend',
    followUp: false,
    render: ({ args: { moduleName, description, previewMediaUrl, actionHref } }) => {
      if (!moduleName || !description) return <></>

      let preview: ReactNode = <PreviewPlaceholder />
      if (previewMediaUrl) {
        if (isVideopreviewMediaUrl(previewMediaUrl)) {
          preview = (
            <video
              src={previewMediaUrl}
              className="h-auto w-full object-cover"
              autoPlay
              playsInline
              muted
              loop
              aria-label="Preview video"
            />
          )
        } else {
          preview = <img src={previewMediaUrl} alt="" className="h-auto w-full object-cover" />
        }
      }
      return (
        <F0DemoCard
          moduleName={moduleName}
          description={description}
          preview={preview}
          actionHref={actionHref}
          onAction={actionHref ? undefined : () => {}}
        />
      )
    },
  })
}
